import React, { useState, useEffect } from 'react';
import api from '../../api';
import AnimatedMapSprite from '../../Pages/Maps/logic/AnimimatedMapSprite';
import { rockIcons } from '../../Pages/Maps/MapConfig/rockIcons';

const CraftModal = ({ 
  isOpen, 
  toggle, 
  spawnText,
  playerPos
}) => {
  // State management
  const [loading, setLoading] = useState(false);
  const [miningInfo, setMiningInfo] = useState(null);
  const [shopItems, setShopItems] = useState([]);
  const [loadingShop, setLoadingShop] = useState(true);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // Load data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchMiningInfo = async () => {
      try {
        setLoadingInfo(true);
        const response = await api.get('/v1/mining/info', { withCredentials: true });
        setMiningInfo(response.data);
      } catch (err) {
        console.error('Error loading mining info:', err);
        if (spawnText && playerPos) {
          spawnText('Failed to load mining info', playerPos.x, playerPos.y - 20, '#f87171');
        }
      } finally {
        setLoadingInfo(false);
      }
    };

    const fetchShopItems = async () => {
      try {
        setLoadingShop(true);
        const response = await api.get('/v1/mining/shop', { withCredentials: true });
        setShopItems(response.data.items || []);
      } catch (err) {
        console.error('Error loading shop items:', err);
        if (spawnText && playerPos) {
          spawnText('Failed to load craft items', playerPos.x, playerPos.y - 20, '#f87171');
        }
      } finally {
        setLoadingShop(false);
      }
    };

    fetchMiningInfo();
    fetchShopItems();
  }, [isOpen, spawnText, playerPos]);

  const handleCraft = async (shopItemId) => {
    const item = shopItems.find(i => i.id === shopItemId);
    setErrorMessage(null);
    if (!item) return;

    try {
      setLoading(true);
      await api.post(
        '/v1/mining/shop/purchase',
        { shop_item_id: shopItemId, quantity: 1 },
        { withCredentials: true }
      );
      
      // Close the modal first
      toggle();
      
      // Create screen flash effect
      const flashOverlay = document.createElement('div');
      flashOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: linear-gradient(45deg, #4ade80, #22d3ee, #a855f7);
        z-index: 9999;
        opacity: 0;
        pointer-events: none;
        animation: craftFlash 1.5s ease-out forwards;
      `;
      
      // Add keyframes for the flash animation
      if (!document.querySelector('#craft-flash-styles')) {
        const style = document.createElement('style');
        style.id = 'craft-flash-styles';
        style.textContent = `
          @keyframes craftFlash {
            0% { opacity: 0; }
            15% { opacity: 0.8; }
            30% { opacity: 0.4; }
            45% { opacity: 0.7; }
            60% { opacity: 0.2; }
            75% { opacity: 0.5; }
            100% { opacity: 0; }
          }
        `;
        document.head.appendChild(style);
      }
      
      document.body.appendChild(flashOverlay);
      
      // Show success message with floating text after a short delay
      setTimeout(() => {
        if (spawnText && playerPos) {
          spawnText(`Crafted ${item.poke_name}!`, playerPos.x, playerPos.y - 20, '#4ade80', `/sprites/${item.poke_name}.png`);
        }
      }, 200);
      
      // Remove flash overlay after animation
      setTimeout(() => {
        if (document.body.contains(flashOverlay)) {
          document.body.removeChild(flashOverlay);
        }
      }, 1500);
      
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || 'Craft failed');
   if (spawnText && playerPos) {
      spawnText(err.response?.data?.detail || 'Craft failed', playerPos.x, playerPos.y - 20, '#f87171');
    }
    } finally {
      setLoading(false);
    }
  };

  const canAffordItem = (item) => {
    if (!miningInfo) return false;
    
    // Check if user has reached their crafting limit
    const remainingCrafts = Math.max(0, item.max_obtainable_purchase_for_player - item.purchased_count);
    if (remainingCrafts <= 0) return false;
    
    // Check gem cost
    if (item.cost_gems > 0) {
      const userGems = miningInfo.resources?.[item.gem_type] || 0;
      if (userGems < item.cost_gems) return false;
    }
    
    // Check rock cost
    if (item.cost_rocks > 0) {
      const userRocks = miningInfo.resources?.rock || 0;
      if (userRocks < item.cost_rocks) return false;
    }
    
    return true;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px 10px',
      boxSizing: 'border-box',
      zIndex: 1050
    }}>
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '900px',
          maxHeight: 'calc(100vh - 40px)',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Craft Pokemon</h3>
          </div>
          <button
            onClick={toggle}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '20px',
          overflowY: 'auto'
        }}>
          {errorMessage && (
    <div style={{
      marginBottom: 16,
      padding: '8px 12px',
      background: 'rgba(255,0,0,0.1)',
      color: '#f87171',
      borderRadius: 4,
      textAlign: 'center',
      fontSize: 14,
    }}>
      {errorMessage}
    </div>
  )}

          {/* Description */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h4 style={{ color: '#f1c40f', marginBottom: '8px', fontSize: '14px' }}>Pokemon Crafting</h4>
            <p style={{ color: '#ccc', fontSize: '12px', margin: 0 }}>
              Use your gems, rocks, and existing Pokemon to craft special variants! 
              Each variant has limits on how many may ever exist, and how many you personally may craft.
            </p>
          </div>

          

          {/* Loading States */}
          {(loadingInfo || loadingShop) && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#ccc'
            }}>
              Loading craft data...
            </div>
          )}

          

          {/* Craft Items */}
          {!loadingInfo && !loadingShop && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {shopItems.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#ccc'
                }}>
                  No craftable Pokemon available at this time.
                </div>
              ) : (
                shopItems.map(item => {
                  const baseName = item.poke_name.replace(
                    /^(Shiny|Golden|Shadow|Emerald|Ruby|Sapphire|Gold|Diamond)/, ''
                  );
                  const canAfford = canAffordItem(item);

                  return (
                    <div key={item.id} style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      gap: '20px',
                      alignItems: 'center'
                    }}>
                      {/* Pokemon Sprites */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '140px'
                      }}>
                        <img
                          src={`/sprites/${item.poke_name}.png`}
                          alt={item.poke_name}
                          width={100}
                          height={100}
                          style={{ imageRendering: 'pixelated' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>

                          <AnimatedMapSprite
                            pokeName={item.poke_name}
                            style={{ width: 24, height: 24 }}
                          />
                        </div>
                      </div>

                      {/* Pokemon Details */}
                      <div style={{ flex: 1 }}>
                        <h5 style={{ color: '#fff', marginBottom: '12px', fontSize: '16px' }}>
                          {item.poke_name}
                        </h5>

                        {/* Limits */}
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                          gap: '8px',
                          marginBottom: '16px',
                          fontSize: '11px'
                        }}>
                          <div style={{ color: '#ccc' }}>
                            <strong style={{ color: '#fff' }}>Max Rarity:</strong> {item.max_rarity}
                          </div>
                          <div style={{ color: '#ccc' }}>
                            <strong style={{ color: '#fff' }}>You've Crafted:</strong> {item.purchased_count}
                          </div>
                          <div style={{ color: '#ccc' }}>
                            <strong style={{ color: '#fff' }}>Can Still Craft:</strong> {Math.max(0, item.max_obtainable_purchase_for_player - item.purchased_count)}
                          </div>
                        </div>

                        {/* Costs */}
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '16px',
                          marginBottom: '16px'
                        }}>
                          {item.cost_gems > 0 && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              padding: '6px 10px',
                              borderRadius: '4px'
                            }}>
                              <img
                                src={rockIcons[item.gem_type]}
                                alt={item.gem_type}
                                width={16}
                                height={16}
                              />
                              <span style={{ fontSize: '12px', color: '#fff' }}>
                                {item.cost_gems} {item.gem_type.charAt(0).toUpperCase() + item.gem_type.slice(1)}
                              </span>
                            </div>
                          )}
                          
                          {item.cost_rocks > 0 && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              padding: '6px 10px',
                              borderRadius: '4px'
                            }}>
                              <img
                                src={rockIcons.rock}
                                alt="rock"
                                width={16}
                                height={16}
                              />
                              <span style={{ fontSize: '12px', color: '#fff' }}>
                                {item.cost_rocks} Rock
                              </span>
                            </div>
                          )}

                          {item.cost_poke > 0 && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              padding: '6px 10px',
                              borderRadius: '4px'
                            }}>
                              <AnimatedMapSprite
                                pokeName={baseName}
                                style={{ width: 16, height: 16 }}
                              />
                              <span style={{ fontSize: '12px', color: '#fff' }}>
                                {item.cost_poke}× {baseName}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Craft Button */}
                        <button
                          onClick={() => handleCraft(item.id)}
                          disabled={!canAfford || loading}
                          style={{
                            background: canAfford ? 'rgba(52, 152, 219, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                            border: canAfford ? '1px solid rgba(52, 152, 219, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            cursor: canAfford && !loading ? 'pointer' : 'not-allowed',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            transition: 'all 0.2s ease',
                            opacity: canAfford && !loading ? 1 : 0.6
                          }}
                          onMouseEnter={(e) => {
                            if (canAfford && !loading) {
                              e.target.style.background = 'rgba(52, 152, 219, 1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (canAfford && !loading) {
                              e.target.style.background = 'rgba(52, 152, 219, 0.8)';
                            }
                          }}
                        >
                          {loading ? 'Crafting...' : canAfford ? 'Craft Pokemon' : 'Cannot Craft'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Resources Display */}
          {miningInfo && (
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              padding: '16px',
              borderRadius: '8px',
              marginTop: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h4 style={{ color: '#f1c40f', marginBottom: '12px', fontSize: '14px' }}>Your Resources:</h4>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '12px' 
              }}>
                {Object.entries(miningInfo.resources || {}).map(([key, value]) => (
                  <div key={key} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    padding: '6px 10px',
                    borderRadius: '4px'
                  }}>
                    <img
                      src={rockIcons[key]}
                      alt={key}
                      width={16}
                      height={16}
                    />
                    <span style={{ fontSize: '12px', color: '#fff' }}>
                      {value} {key.charAt(0).toUpperCase() + key.slice(1)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CraftModal;
